import { easasAlert } from "components/fragments/easas-global-fragment/EasasGlobalAlerts";

import { TaskInputGroup } from "api/model/airflow-task/TaskInputGroup";
import JSONEditor from "components/basic/json-editor/JSONEditor";
import { PermissionGuard } from "components/wrappers/PermissionGuard";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { InputSwitch } from "primereact/inputswitch";
import { InputText } from "primereact/inputtext";
import React, { useRef, useState } from "react";
import { T } from "services/lang/LanguageService";
import { JSONEditorMode, MapperEditorMode, MapperOperationObject } from "utils/enums";
import { v4 } from "uuid";
import { DatasetStringValueMapperDTO, MappingEntryDto } from "../../../charts/interfaces";

interface CreateEditDatasetMapperProps {
  mapperData?: DatasetStringValueMapperDTO;
  mode: MapperEditorMode;
  isVisible: boolean;
  setIsVisible: (isVisible: boolean) => void;
  handleDownloadClick: (
    mapperData: DatasetStringValueMapperDTO,
    operationObject: MapperOperationObject
  ) => void;
  onSave: (data: DatasetStringValueMapperDTO) => void;
}

const CreateEditDatasetMapper = ({
  mapperData,
  mode,
  isVisible,
  setIsVisible,
  onSave,
  handleDownloadClick,
}: CreateEditDatasetMapperProps) => {
  const initialMappings = mapperData
    ? mapperData.mappers.map(m => ({
        id: m.id,
        key: String(m.key),
        value: m.label,
        icon: m.icon ?? "",
      }))
    : [{ id: v4(), key: "", value: "", icon: "" }];

  const fileInputRef = useRef<HTMLInputElement>(null);

  const [mappingsList, setMappingsList] =
    useState<{ id: string; key: string; value: string; icon: string }[]>(initialMappings);

  const [isJSONEditorVisible, setIsJSONEditorVisible] = useState<boolean>(false);

  const [currentMapper, setCurrentMapper] = useState<DatasetStringValueMapperDTO>({
    id: mapperData?.id,
    title: mapperData?.title ?? "",
    global: mapperData?.global ?? false,
    company: mapperData?.company,
    mappers: mapperData?.mappers ?? [],
    mappings: [],
  });

  const toMappingEntries = (
    list: { id: string; key: string; value: string; icon: string }[]
  ): MappingEntryDto[] =>
    list
      .filter(m => m.key !== "")
      .map(m => ({ id: m.id, key: Number(m.key), label: m.value, icon: m.icon || undefined }));

  const handleMappingChange = (index: number, field: "key" | "value" | "icon", newValue: string) => {
    const updated = [...mappingsList];
    updated[index] = { ...updated[index], [field]: newValue };
    setMappingsList(updated);
    setCurrentMapper(prev => ({ ...prev, mappers: toMappingEntries(updated) }));
  };

  const addMapping = () => {
    setMappingsList(prev => [...prev, { id: v4(), key: "", value: "", icon: "" }]);
  };

  const removeMapping = (index: number) => {
    const updatedMappings = mappingsList.filter((_, i) => i !== index);
    setMappingsList(updatedMappings);
    setCurrentMapper(prev => ({ ...prev, mappers: toMappingEntries(updatedMappings) }));
  };

  const handleSave = () => {
    if (currentMapper.title.trim() === "") {
      easasAlert({
        summary: T("alert.title-missing"),
        detail: T("alert.please-enter-a-title"),
        severity: "error",
      });
      return;
    }

    if (currentMapper.mappers.length === 0) {
      easasAlert({
        summary: T("alert.mappings-missing"),
        detail: T("alert.please-enter-at-least-one-mapping"),
        severity: "error",
      });
      return;
    }

    onSave(currentMapper);
  };

  const handleJSONEditorChange = (e: DatasetStringValueMapperDTO | Record<string, TaskInputGroup[]>) => {
    if ("title" in e && "mappings" in e) {
      const updatedMapper = e as DatasetStringValueMapperDTO;

      const updatedList = updatedMapper.mappers.map(m => ({
        id: m.id,
        key: String(m.key),
        value: m.label,
        icon: m.icon ?? "",
      }));

      setCurrentMapper(updatedMapper);
      setMappingsList(updatedList);
    } else {
      easasAlert({
        summary: T("alert.json-is-not-valid"),
        detail: T("easas-alert.invalid-mapper-format"),
        severity: "error",
      });
    }
  };

  const footerTemplate = (
    <div className="flex flex-row justify-content-between">
      <PermissionGuard permissions="HIDDEN_FEATURE_ACCESS">
        <Button
          label={T("word.json-editor")}
          icon="pi pi-code"
          className="component-editor-button icon-blue"
          onClick={() => setIsJSONEditorVisible(true)}
        />
      </PermissionGuard>
      <div className="flex flex-row gap-2">
        <Button
          className="component-editor-button"
          icon="pi pi-times"
          label={T("word.cancel")}
          onClick={() => setIsVisible(false)}
        />
        <Button
          className="component-editor-button icon-green"
          icon="pi pi-save"
          label={mode === MapperEditorMode.CREATE ? T("action.create") : T("action.save")}
          onClick={handleSave}
        />
      </div>
    </div>
  );

  const handleMappingsUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      const text = await file.text();
      const parsed: MappingEntryDto[] = JSON.parse(text);

      if (
        !Array.isArray(parsed) ||
        parsed.length === 0 ||
        !parsed.every(m => typeof m.key === "number" && typeof m.label === "string")
      ) {
        throw new Error(T("easas-alert.invalid-mappings-format"));
      }

      const existingKeys = new Set(currentMapper.mappers.map(m => m.key));
      const newEntries: MappingEntryDto[] = parsed
        .filter(m => !existingKeys.has(m.key))
        .map(m => ({ id: m.id ?? v4(), key: m.key, label: m.label, icon: m.icon }));

      const mergedMappings = [...currentMapper.mappers, ...newEntries];

      const updatedList = mergedMappings.map(m => ({
        id: m.id,
        key: String(m.key),
        value: m.label,
        icon: m.icon ?? "",
      }));

      setMappingsList(updatedList);
      setCurrentMapper(prev => ({ ...prev, mappers: mergedMappings }));
    } catch (error) {
      easasAlert({
        summary: T("alert.json-is-not-valid"),
        detail: (error as Error).message,
        severity: "error",
      });
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  return (
    <Dialog
      style={{ width: "min(100vw, 500px)", maxHeight: "600px" }}
      header={T("action.edit-mapper")}
      visible={isVisible}
      onHide={() => setIsVisible(false)}
      footer={footerTemplate}
      resizable
    >
      <div className="flex flex-column gap-3">
        <div className="flex flex-row justify-content-between">
          <p className="w-6 mb-0 align-self-center">{T("label.mapper.dataset-mapper-title")}</p>
          <InputText
            className="w-full"
            value={currentMapper.title}
            onChange={e => setCurrentMapper(prev => ({ ...prev, title: e.target.value }))}
            placeholder={T("label.mapper.new-dataset-mapper-title")}
          />
        </div>
        <PermissionGuard permissions="ADMIN">
          <div className="flex col-12 align-items-center">
            <p>{T("word.global")}</p>
            <InputSwitch
              checked={currentMapper.global}
              onChange={e => setCurrentMapper(prev => ({ ...prev, global: e.value }))}
            />
          </div>
        </PermissionGuard>
        <div className="flex flex-row justify-content-between">
          <div className="font-bold text-xl">{T("word.mappings")}</div>
          <div className="flex flex-row gap-2">
            <input
              type="file"
              accept="application/json"
              ref={fileInputRef}
              style={{ display: "none" }}
              onChange={handleMappingsUpload}
            />
            <Button
              type="button"
              icon="pi pi-upload"
              tooltip={T("action.upload-mappings-json")}
              tooltipOptions={{ position: "bottom" }}
              label={T("action.upload")}
              className="component-editor-button icon-green"
              onClick={() => fileInputRef.current?.click()}
            />
            <Button
              type="button"
              icon="pi pi-download"
              tooltip={T("action.download-mappings-json")}
              tooltipOptions={{ position: "bottom" }}
              label={T("action.download")}
              className="component-editor-button icon-blue"
              onClick={() => handleDownloadClick(currentMapper, MapperOperationObject.MAPPINGS)}
            />
            <Button
              icon="pi pi-file"
              label={T("label.icons-list")}
              className="component-editor-button"
              onClick={() => window.open("https://primereact.org/icons/", "_blank")}
            />
          </div>
        </div>
        {mappingsList.map((mapping, index) => (
          <div key={index} className="flex w-12 align-items-center gap-2">
            <InputText
              className="w-full"
              value={mapping.key}
              onChange={e => handleMappingChange(index, "key", e.target.value)}
              placeholder={T("word.key")}
            />

            <InputText
              className="w-full"
              value={mapping.value}
              onChange={e => handleMappingChange(index, "value", e.target.value)}
              placeholder={T("word.value")}
            />

            <InputText
              className="w-full"
              value={mapping.icon}
              onChange={e => handleMappingChange(index, "icon", e.target.value)}
              placeholder={T("word.icon")}
              tooltip="ex: pi pi-home"
              tooltipOptions={{ position: "bottom" }}
            />

            <Button
              type="button"
              icon="pi pi-trash"
              className="const-del-btn  p-button-rounded p-button-text p-button-danger"
              onClick={() => removeMapping(index)}
            />
          </div>
        ))}
        <div className="w-full">
          <Button
            className="component-editor-button icon-green"
            icon="pi pi-plus"
            label={T("action.add-mapping")}
            onClick={addMapping}
          />
        </div>
      </div>
      {isJSONEditorVisible && (
        <JSONEditor
          config={currentMapper}
          isVisible={isJSONEditorVisible}
          setIsVisible={setIsJSONEditorVisible}
          mode={JSONEditorMode.MAPPER}
          onMapperChange={handleJSONEditorChange}
        />
      )}
    </Dialog>
  );
};

export default CreateEditDatasetMapper;
